import {
  Component,
  Inject,
  NgZone,
  OnInit,
  PLATFORM_ID,
  Input,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
// amCharts imports
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { FTICCohortStat } from '../../../../../server/types';
import { DataService } from '@/shared/services';

@Component({
  selector: 'apr-trend-graph',
  templateUrl: './apr-trend-graph.component.html',
  styleUrls: ['./apr-trend-graph.component.css'],
  standalone: false,
})
export class AprTrendGraphComponent implements OnInit {
  @Input({ required: true }) chartData!: FTICCohortStat[];

  private root!: am5.Root;
  private chart!: am5xy.XYChart;

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private zone: NgZone,
    private dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.generateGraph();
  }

  ngOnChanges() {
    this.generateGraph();
  }

  generateGraph() {
    /* Chart code */
    // Create root element
    // https://www.amcharts.com/docs/v5/getting-started/#Root_element
    if (!this.root) this.root = am5.Root.new('chartdiv');

    // Remove amcharts logo below graphs
    this.root._logo!.dispose();

    // Set themes
    // https://www.amcharts.com/docs/v5/concepts/themes/
    this.root.setThemes([am5themes_Animated.new(this.root)]);

    // // if chart is not empty then erase it for new data coming
    if (this.chart) {
      this.chart.series.pop();
      this.root.container.children.pop();
    }

    this.root._logo!.dispose();

    // Create chart
    // https://www.amcharts.com/docs/v5/charts/xy-chart/
    this.chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panX: false,
        panY: false,
        wheelX: 'panX',
        wheelY: 'zoomX',
        layout: this.root.verticalLayout,
        cursor: am5xy.XYCursor.new(this.root, {}),
      }),
    );

    const cursor = this.chart.get('cursor');

    cursor?.lineX.setAll({
      visible: false,
    });

    cursor?.lineY.setAll({
      visible: false,
    });

    // Create axes
    // https://www.amcharts.com/docs/v5/charts/xy-chart/axes/
    const xRenderer = am5xy.AxisRendererX.new(this.root, {
      minGridDistance: 30,
    });

    const xAxis = this.chart.xAxes.push(
      am5xy.CategoryAxis.new(this.root, {
        categoryField: 'cohort',
        renderer: xRenderer,
      }),
    );

    xRenderer.grid.template.setAll({
      location: 1,
    });

    xRenderer.labels.template.setAll({
      paddingTop: 20,
    });

    xAxis.data.setAll(this.chartData);

    // Remove commas from the x-axis labels
    xAxis.get('renderer').labels.template.set(
      'numberFormatter',
      am5.NumberFormatter.new(this.root, {
        numberFormat: '#',
      }),
    );

    const yAxis = this.chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {
          strokeOpacity: 0.1,
        }),
      }),
    );

    const paretoAxisRenderer = am5xy.AxisRendererY.new(this.root, {
      opposite: true,
    });

    const paretoAxis = this.chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: paretoAxisRenderer,
        min: 0,
        max: 100,
        strictMinMax: true,
      }),
    );

    paretoAxisRenderer.grid.template.set('forceHidden', true);
    paretoAxis.set('numberFormat', '#');

    // Add series
    // https://www.amcharts.com/docs/v5/charts/xy-chart/series/
    var series1 = this.chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        name: 'Cohort Size',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'cohort_size',
        categoryXField: 'cohort',
        legendLabelText: '{name}',
        legendRangeLabelText: '{name}',
      }),
    );
    series1.data.setAll(this.chartData);
    series1.set('fill', am5.color(0x5aaa95));

    var series2 = this.chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        name: 'Retention',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'retention',
        categoryXField: 'cohort',
        legendLabelText: '{name}',
        legendRangeLabelText: '{name}',
      }),
    );
    series2.data.setAll(this.chartData);
    series2.set('fill', am5.color(0xb30000));

    var series3 = this.chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        name: 'APR',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'apr_total',
        categoryXField: 'cohort',
        legendLabelText: '{name}',
        legendRangeLabelText: '{name}',
      }),
    );
    series3.data.setAll(this.chartData);
    series3.set('fill', am5.color(0x6794dc));

    var series4 = this.chart.series.push(
      am5xy.ColumnSeries.new(this.root, {
        name: 'On-Time Grad',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'on_time',
        categoryXField: 'cohort',
        legendLabelText: '{name}',
        legendRangeLabelText: '{name}',
      }),
    );
    series4.data.setAll(this.chartData);
    series4.set('fill', am5.color(0x4b0092)); // change color for colorblind adaptation

    series1.columns.template.setAll({
      tooltipText: '[bold]{name}[/]\n{categoryX}: {valueY}',
      tooltip: am5.Tooltip.new(this.root, {
        labelText: '[bold]{name}[/]\n{categoryX.formatNumber("#")}: {valueY}',
      }),
      tooltipY: 0,
      strokeOpacity: 0,
      cornerRadiusTL: 6,
      cornerRadiusTR: 6,
    });

    series2.columns.template.setAll({
      tooltipText: '[bold]{name}[/]\n{categoryX}: {valueY}',
      tooltip: am5.Tooltip.new(this.root, {
        labelText: '[bold]{name}[/]\n{categoryX.formatNumber("#")}: {valueY}',
      }),
      tooltipY: 0,
      strokeOpacity: 0,
      cornerRadiusTL: 6,
      cornerRadiusTR: 6,
    });

    series3.columns.template.setAll({
      tooltipText: '[bold]{name}[/]\n{categoryX}: {valueY}',
      tooltip: am5.Tooltip.new(this.root, {
        labelText: '[bold]{name}[/]\n{categoryX.formatNumber("#")}: {valueY}',
      }),
      tooltipY: 0,
      strokeOpacity: 0,
      cornerRadiusTL: 6,
      cornerRadiusTR: 6,
    });

    series4.columns.template.setAll({
      tooltipText: '[bold]{name}[/]\n{categoryX}: {valueY}',
      tooltip: am5.Tooltip.new(this.root, {
        labelText: '[bold]{name}[/]\n{categoryX.formatNumber("#")}: {valueY}',
      }),
      tooltipY: 0,
      strokeOpacity: 0,
      cornerRadiusTL: 6,
      cornerRadiusTR: 6,
    });

    const dataLabelConfig = () =>
      am5.Bullet.new(this.root, {
        locationY: 0.5,
        sprite: am5.Label.new(this.root, {
          text: '{valueY}',
          fill: am5.color(0xffffff),
          centerY: am5.p50,
          centerX: am5.p50,
          populateText: true,
          shadowColor: am5.color(0x000),
          shadowBlur: 5,
          shadowOffsetX: 2,
          shadowOffsetY: 2,
          rotation: -90,
        }),
      });

    series1.bullets.push(dataLabelConfig);
    series2.bullets.push(dataLabelConfig);
    series3.bullets.push(dataLabelConfig);
    series4.bullets.push(dataLabelConfig);

    // pareto series
    const paretoSeries = this.chart.series.push(
      am5xy.LineSeries.new(this.root, {
        name: 'Apr%',
        xAxis: xAxis,
        yAxis: paretoAxis,
        valueYField: 'apr_total_pct',
        categoryXField: 'cohort',
        stroke: this.root.interfaceColors.get('alternativeBackground'),
        maskBullets: false,
        legendLabelText: '{name}',
        legendRangeLabelText: '{name}',
        tooltip: am5.Tooltip.new(this.root, {
          pointerOrientation: 'right',
          labelText:
            '[bold]{name}[/]\n{categoryX.formatNumber("#")}: {valueY}%',
        }),
      }),
    );

    paretoSeries.bullets.push(() => {
      return am5.Bullet.new(this.root, {
        locationY: 1,
        sprite: am5.Circle.new(this.root, {
          radius: 6,
          stroke: this.root.interfaceColors.get('alternativeBackground'),
          strokeWidth: 2,
          fill: paretoSeries.get('fill'),
        }),
      });
    });

    paretoSeries.bullets.push(() => {
      return am5.Bullet.new(this.root, {
        locationY: 1,
        sprite: am5.Label.new(this.root, {
          text: '{valueY}%',
          fill: am5.color(0x000),
          centerY: am5.p100,
          centerX: am5.p50,
          populateText: true,
        }),
      });
    });

    paretoSeries.data.setAll(this.chartData);

    var legend = this.chart.children.push(am5.Legend.new(this.root, {}));
    legend.data.setAll(this.chart.series.values);

    // Make stuff animate on load
    // https://www.amcharts.com/docs/v5/concepts/animations/
    series1.appear();
    series2.appear();
    this.chart.appear(1000, 100);
  }

  // Run the function only in the browser
  browserOnly(f: () => void) {
    if (isPlatformBrowser(this.platformId)) {
      this.zone.runOutsideAngular(() => {
        f();
      });
    }
  }

  ngOnDestroy() {
    // Clean up chart when the component is removed
    this.browserOnly(() => {
      if (this.root) {
        this.root.dispose();
      }
    });
  }

  exportChart() {
    this.dataService.exportAmChartsAsPNG(this.root, 'apr-trend-graph');
  }
}
